import {
  LinearProgress as JoyLinearProgress,
  LinearProgressProps,
  styled,
  Typography,
} from '@mui/joy';
import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Color, Level, Size, Variant } from 'theme/enums';

const DEFAULT_THICKNESS = 12;

const shouldForwardProp = (prop: string) => prop !== 'customSxProps';

const LinearProgress = styled(JoyLinearProgress, { shouldForwardProp })<{
  customSxProps?: CSSProperties;
}>(({ theme, customSxProps }) => ({
  backgroundColor: theme.palette.primary[100],
  '--LinearProgress-radius': '6px',
  ...customSxProps,
}));

const Label = styled(Typography)<{ value: number }>(({ theme, value }) => ({
  position: 'absolute',
  left: value ? `calc(${value}% - 20px)` : '50%',
  transform: 'translateX(-50%)',
  fontWeight: 700,
  color: value ? theme.palette.text.primary : theme.palette.neutral[600],
}));

export const LinearProgressWithValue = ({
  value,
  sx: customSxProps,
  ...rest
}: LinearProgressProps): JSX.Element | null => {
  const [isVisible, setIsVisible] = useState(value <= 100);

  const { t } = useTranslation();

  useEffect(() => {
    if (value === 100) {
      setTimeout(() => {
        setIsVisible(false);
      }, 1000);
    }
  }, [value]);

  if (!isVisible) {
    return null;
  }

  return (
    <LinearProgress
      determinate
      variant={Variant.SOFT}
      color={Color.SUCCESS}
      size={Size.SM}
      thickness={DEFAULT_THICKNESS}
      value={value}
      customSxProps={customSxProps as CSSProperties}
      {...rest}
    >
      <Label level={Level.BODY_XS} value={value}>
        {!value ? t('messageLoading') : `${value}%`}
      </Label>
    </LinearProgress>
  );
};
